import { FC, memo } from 'react';

import { IoCloudUploadOutline , IoCloseCircle, IoImageOutline, IoVideocamOutline } from 'react-icons/io5';
import { TiInfoOutline } from "react-icons/ti";

import { MdInsertDriveFile } from 'react-icons/md';
import { Divider, LinearProgress } from '@mui/material';

import { OInput } from '@src/shared';

import styles from './setupSection.module.scss';
import { 
  formatFileSize,
  isFileExtensionOneOf
} from '@src/utils/files.utils';
import { useSetupSection } from './useSetupSection';
import { 
    MAX_FILES, 
    SUPPORTED_EXTENSIONS, 
    VIDEO_EXTENSIONS, 
    IMAGE_EXTENSIONS 
} from './setupSection.constants';

interface IProps {
  buttonText: string;
  buttonUrl: string;
  onButtonTextChange: (val: string) => void;
  onButtonUrlChange: (val: string) => void;
  uploadedFiles: File[];
  onFilesChange: (files: File[]) => void;
  ctaDurationInSeconds: number;
  urlError: string | null;
  onUrlBlur: () => void;
}

const SetupSection: FC<IProps> = ({ 
  buttonText, 
  buttonUrl, 
  onButtonTextChange, 
  onButtonUrlChange,
  uploadedFiles,
  onFilesChange,
  ctaDurationInSeconds,
  urlError,
  onUrlBlur
}) => {
  const {
    fileInputRef,
    isDragging,
    isUploading,
    uploadError,
    uploadingFiles,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleBrowseClick,
    handleFileInputChange,
    handleRemoveFile
  } = useSetupSection({
    uploadedFiles,
    onFilesChange,
    ctaDurationInSeconds
  });

  const getFileIcon = (fileName: string) => {
    if (isFileExtensionOneOf(fileName, VIDEO_EXTENSIONS)) {
        return <IoVideocamOutline size={20} color="#00ACFB" />;
    }
    
    if (isFileExtensionOneOf(fileName, IMAGE_EXTENSIONS)) {
        return <IoImageOutline size={20} color="#00ACFB" />;
    }
    
    return <MdInsertDriveFile size={20} color="#00ACFB" />;
  };

  const allFiles = [
      ...uploadedFiles.map(f => ({ file: f, isUploading: false, progress: 0, remaining: 0 })), 
      ...uploadingFiles.map(w => ({ file: w.file, isUploading: true, progress: w.progress, remaining: w.remainingSeconds }))
  ];

  return (
    <div className={styles.sectionBox}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionTitle}>CTA Setup</div>
        <div className={styles.setupSubtitle}>
          You can either add a button with a link or upload media—one of them is required.
        </div>
        <Divider className={styles.divider} sx={{ height:'0px'}} />
      </div>

      <div className={styles.setupRow}>
        <div className={styles.fieldGroup}>
          <span className={styles.label}>Button Text</span>
          <OInput
            value={buttonText}
            onChange={(e) => onButtonTextChange(e.target.value)}
            placeholder="Enter button text"
            maxLength={50}
            extension={`${buttonText.length}/50`}
          />
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.label}>URL</span>
          <OInput 
            value={buttonUrl} 
            onChange={(e) => onButtonUrlChange(e.target.value)} 
            onBlur={onUrlBlur}
            placeholder="Paste link here" 
            invalid={!!urlError}
            note={urlError || undefined}
          />
        </div>
      </div>

      {/* Media Upload */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={SUPPORTED_EXTENSIONS.join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />
      
      <div 
        className={`${styles.dropzone} ${isDragging ? styles.dropzoneDragging : ''} ${allFiles.length >= MAX_FILES ? styles.dropzoneDisabled : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={allFiles.length < MAX_FILES && !isUploading ? handleBrowseClick : undefined}
      >
        <IoCloudUploadOutline size={32} color="#00ACFB" />
        <div className={styles.dropzoneText}>
          Drag your file(s) or <span className={styles.browseText}>browse</span>
          <br />
          <span className={styles.dropzoneNote}>Max {MAX_FILES} Media files are allowed</span>
        </div>
      </div>
      
      {uploadError && (
        <div className={styles.errorBox}>
          <TiInfoOutline size={20} color="#D96363" />
          {uploadError}
        </div>
      )}
      
      {/* Uploaded Files List */}
      {allFiles.length > 0 && (
        <div className={styles.filesContainer}>
          {allFiles.map((item, index) => {
            const file = item.file;
            return (
              <div key={`${file.name}-${index}`}>
                {item.isUploading ? (
                  <div className={styles.uploadingItem}>
                      <p className={styles.uploadingItemHeader}>Uploading...</p>
                      <div className={styles.uploadingItemText}>
                        <p>{Math.round(item.progress)}%</p>
                          • 
                        <p>{item.remaining}s remaining</p>
                      </div>
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      sx={{ 
                          borderRadius: 5,
                          height: '8px',
                          marginTop:"0.5rem",
                          backgroundColor:'#FAFAFA',
                          '& .MuiLinearProgress-bar': {
                              backgroundColor: '#00ACFB',
                              borderRadius: 5,
                          },
                      }}
                    />
                  </div>
                ) : (
                   <div className={styles.fileItem}>
                     <div className={styles.fileInfo}>
                       {getFileIcon(file.name)}
                        <div>
                         <div className={styles.fileName}>{file.name}</div>
                         <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                       </div>
                     </div>
                     <button
                       type="button"
                       className={styles.removeButton}
                       onClick={() => handleRemoveFile(index)}
                       aria-label="Remove file"
                     >
                       <IoCloseCircle size={20} color="#f4d1d1"/>
                     </button>   
                   </div>
                 )}
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.supportedFormats}>
        Supported formats: Images (.jpg, .jpeg, .png, .gif, .svg), Videos (.mp4, .mov, .avi, .webm), QR codes (.jpg, .png, .svg), and Documents (.pdf, .doc,<br/> .docx, .xls, .xlsx, .csv, .ppt, .pptx).
      </div>
    </div>
  );
};

export default memo(SetupSection);
